import React from 'react';
import { useTranslation } from 'react-i18next';

import { EmailLog } from 'models';

import {
    Button,
    Card,
    Dropdown,
    DropdownItem,
    DropdownSort,
    Filter,
    FilterSetController,
    Icon,
    Status,
    Table,
    TableCell,
    TableBody,
    TableRow,
    TableCellDropdown,
    ToastContext,
    WeezHeader,
    EmptyCard
} from '@weezevent/nacre';

import { LoadingCard, Paginator } from 'components';
import { useFilters, usePageSize, useCurrentPage, useSearch, handleSearchQuery } from 'hooks/useFilters';

import css from './emaillogs.module.css';
import { ProductOptions, Products } from '../../../utils/products';
import { DateTimeFilterModal } from './components/DateTimeFilterModal';
import { formatDateOnly, formatHourOnly } from '../../../utils/utils';
import { EmailLogsDetails } from './EmailLogsDetails';

const spec = {
    multiple: ['last_action_event_type__in', 'template__application__in'],
    fields: ['last_action_event_type__in', 'template__application__in', 'send_date_after', 'send_date_before'],
    date: [],
    form: [],
    aliases: {}
};

var currentDate = new Date();
const sevenDaysAgo = new Date();
sevenDaysAgo.setDate(currentDate.getDate() - 7);

const initialFilters = {
    send_date_after: sevenDaysAgo.toJSON(),
    send_date_before: currentDate.toJSON()
};

const STATUS_CONFIG = [
    { id: 'Open', color: 'green' },
    { id: 'Click', color: 'green' },
    { id: 'Delivery', color: 'green' },
    { id: 'Send', color: 'orange' },
    { id: 'Soft-Bounce', color: 'red' },
    { id: 'Hard-Bounce', color: 'red' },
    { id: 'fake-delivered', color: '' }
];

export const EmailLogs = ({ organizationId }) => {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);

    const [filters, setFilters] = useFilters(spec, {});
    const [dateFilters, setDateFilters] = React.useState(initialFilters);
    const [perPageCount, setPerPageCount] = usePageSize(100);
    const [currentPage, setCurrentPage] = useCurrentPage(0);
    const [search, setSearch] = useSearch();
    const [ordering, setOrdering] = React.useState('send_date');
    const [dateFilterModalOpen, setDateFilterModalOpen] = React.useState(false);
    const [selectedMessage, setSelectedMessage] = React.useState(null);

    const sortByDates = [
        { key: 'send_date', label: t('common.ordering.ascending') },
        { key: '-send_date', label: t('common.ordering.descending') }
    ];
    const sortByNames = [
        { key: 'template__name', label: t('common.ordering.alphabetical') },
        { key: '-template__name', label: t('common.ordering.-alphabetical') }
    ];
    const sortByEmails = [
        { key: 'to_email', label: t('common.ordering.alphabetical') },
        { key: '-to_email', label: t('common.ordering.-alphabetical') }
    ];

    const [emailLogs, loadingEmailsLogs] = EmailLog.useApiModel(
        {
            organizationId,
            cache: false,
            allow_cache: false,
            strict: true,
            query: {
                limit: perPageCount,
                offset: currentPage * perPageCount,
                ordering,
                search,
                ...handleSearchQuery(spec, { ...filters, ...dateFilters }, true)
            },
            onError: () => {
                toast.error(t('sauron.toasts.error'));
            }
        },
        [filters, dateFilters, perPageCount, currentPage, search, ordering, organizationId]
    );

    const setDateFilter = dates => {
        setDateFilters({ send_date_after: dates.date_after, send_date_before: dates.date_before });
        setDateFilterModalOpen(false);
    };

    const logsCount = React.useMemo(() => {
        if (loadingEmailsLogs) {
            return '--';
        }
        return Number.parseInt(emailLogs?.__meta?.headers.get('total-count')) || 0;
    }, [emailLogs, loadingEmailsLogs]);

    const display_date = date => {
        const rule_in = new RegExp(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}).*/);

        return date?.replace(rule_in, '$3/$2/$1 $4:$5');
    };

    return (
        <>
            <WeezHeader
                rightComponent={[
                    <Button
                        inverted
                        key={0}
                        primary
                        onClick={() => setDateFilterModalOpen(true)}
                        label={
                            <>
                                <Icon name="calendar" />
                                {display_date(dateFilters?.send_date_after)} - {display_date(dateFilters?.send_date_before)}
                            </>
                        }
                    />
                ]}
                title={t('sauron.emails.email-logs.title')}
            >
                {t('sauron.emails.email-logs.subtitle')}
            </WeezHeader>
            <Card>
                <div className={css['card-header']}>
                    <FilterSetController
                        className={css['filter-block']}
                        total={logsCount}
                        labelTotal={t('sauron.emails.email-logs.log', { count: logsCount })}
                        labelFilters={t('sauron.emails.email-logs.filters.filter')}
                        labelReset={t('sauron.emails.email-logs.filters.label-reset')}
                        initialValues={filters}
                        onChange={setFilters}
                        onReset={() => {
                            setPerPageCount(100);
                            setCurrentPage(0);
                            setFilters({});
                        }}
                        search={{
                            onChange: (_, value) => setSearch(value),
                            handleReset: () => setSearch(''),
                            placeholder: t('sauron.emails.email-logs.filters.search'),
                            value: search
                        }}
                    >
                        <Filter
                            index="1"
                            multiple
                            labelSelectAll={t('sauron.emails.email-logs.filters.select-all')}
                            labelName={t('sauron.emails.email-logs.status.label')}
                            labelCTA={t('sauron.emails.email-logs.filters.label-cta')}
                            name="last_action_event_type__in"
                            options={STATUS_CONFIG.slice(0, -1).map(({ id, color }) => ({
                                key: id,
                                name: id,
                                color: color,
                                text: t(`sauron.emails.email-logs.status.${id}`)
                            }))}
                        />
                        <Filter
                            index="2"
                            multiple
                            labelSelectAll={t('sauron.emails.email-logs.filters.select-all')}
                            labelName={t('sauron.emails.email-logs.application')}
                            labelCTA={t('sauron.emails.email-logs.filters.label-cta')}
                            name="template__application__in"
                            options={ProductOptions}
                        />
                    </FilterSetController>
                </div>
                <LoadingCard loading={loadingEmailsLogs}>
                    <Table>
                        <Table.THead>
                            <Table.Tr isHeader>
                                <TableCell className={css['date-row']}>
                                    <DropdownSort
                                        active={['send_date', '-send_date'].includes(ordering)}
                                        label={t('sauron.emails.email-logs.date-sent')}
                                        name="sort"
                                        ordering={ordering}
                                        items={sortByDates.map(item => (
                                            <DropdownItem.Icons key={item.key} item={item.label} checked={ordering === item.key} onClick={() => setOrdering(item.key)} />
                                        ))}
                                    />
                                </TableCell>
                                <TableCell className={css['name-row']}>
                                    <DropdownSort
                                        active={['template__name', '-template__name'].includes(ordering)}
                                        label={t('sauron.emails.email-logs.email-template')}
                                        name="sort"
                                        ordering={ordering}
                                        items={sortByNames.map(item => (
                                            <DropdownItem.Icons key={item.key} item={item.label} checked={ordering === item.key} onClick={() => setOrdering(item.key)} />
                                        ))}
                                    />
                                </TableCell>
                                <TableCell className={css['recipient-row']}>
                                    <DropdownSort
                                        active={['to_email', '-to_email'].includes(ordering)}
                                        label={t('sauron.emails.email-logs.recipient')}
                                        name="sort"
                                        ordering={ordering}
                                        items={sortByEmails.map(item => (
                                            <DropdownItem.Icons key={item.key} item={item.label} checked={ordering === item.key} onClick={() => setOrdering(item.key)} />
                                        ))}
                                    />
                                </TableCell>
                                <TableCell className={css['status-row']}>{t(`sauron.emails.email-logs.status.label`)}</TableCell>
                                <TableCell className={css['actions-row']}>{t(`sauron.emails.email-logs.actions.label`)}</TableCell>
                            </Table.Tr>
                        </Table.THead>

                        {logsCount > 0 &&
                            emailLogs?.map((emailLog, index) => (
                                <TableBody key={index}>
                                    <TableRow className={css['logs-table']} onClick={() => setSelectedMessage(emailLog)}>
                                        <TableCell className={css['date-row']}>
                                            {formatDateOnly(emailLog.send_date)}
                                            <div className={css['card-subtitle']}>{formatHourOnly(emailLog.send_date)}</div>
                                        </TableCell>
                                        <TableCell className={css['name-row']}>
                                            <div className={css['product-container']}>
                                                {emailLog.template_application && Products[emailLog.template_application].logo} {emailLog.template_name}
                                            </div>
                                        </TableCell>
                                        <TableCell className={css['recipient-row']}>{emailLog.to ? emailLog.to[0]['email'] : '-'}</TableCell>
                                        <TableCell className={css['status-row']}>
                                            <Status
                                                color={emailLog.last_action?.event ? STATUS_CONFIG.find(status => status.id === emailLog.last_action?.event)?.color : 'red'}
                                                text={t(`sauron.emails.email-logs.status.${emailLog.last_action?.event || 'error'}`)}
                                            />
                                        </TableCell>
                                        <TableCellDropdown>
                                            <Dropdown.Item key={0} item={t('sauron.emails.email-logs.actions.see-logs')} onClick={() => setSelectedMessage(emailLog)} />
                                        </TableCellDropdown>
                                    </TableRow>
                                </TableBody>
                            ))}
                    </Table>
                    {logsCount === 0 && (
                        <EmptyCard className={css['empty-card']} title={t('sauron.emails.email-logs.no-logs')} subTitle={t('sauron.emails.email-logs.no-logs-subtitle')} />
                    )}
                </LoadingCard>
            </Card>
            {!loadingEmailsLogs && <Paginator currentPage={currentPage} onPageChange={setCurrentPage} pageSize={perPageCount} totalCount={logsCount} />}
            {setDateFilterModalOpen && (
                <DateTimeFilterModal
                    open={dateFilterModalOpen}
                    onClose={() => setDateFilterModalOpen(false)}
                    onSubmit={dates => setDateFilter(dates)}
                    dateAfter={dateFilters.send_date_after}
                    dateBefore={dateFilters.send_date_before}
                />
            )}
            {selectedMessage && (
                <EmailLogsDetails
                    organizationId={organizationId}
                    messageUuid={selectedMessage?.uuid}
                    displayDate={display_date}
                    message={selectedMessage}
                    onClose={() => setSelectedMessage(null)}
                    status_config={STATUS_CONFIG}
                />
            )}
        </>
    );
};
